import {
  PRODUCT_FLAG_ROWS,
  type ProductFlagFormValues,
  type ProductFlagTier,
  type SettableProductFlagField,
  TIER_RANK,
} from '@/definitions/productFlags'

import { hasUsableAccountSid } from './canEnableTwilio'
import { clampToRequirements } from './flagDependencies'

/** The product flags the packages own — everything a package choice is allowed to move. */
const PACKAGE_FIELDS: readonly SettableProductFlagField[] = PRODUCT_FLAG_ROWS.filter(
  (row) => row.tier !== 'manual'
).map((row) => row.field)

/**
 * Whether a row in `rowTier` can be changed while `selectedTier` is chosen.
 *
 * Manual switches are always editable — they are not part of a package. Everything
 * else is editable only up to the selected package, and nothing is when no package
 * is selected at all.
 */
export const isTierEditable = (
  rowTier: ProductFlagTier,
  selectedTier: ProductFlagTier | null
): boolean =>
  rowTier === 'manual' ||
  (selectedTier !== null && TIER_RANK[rowTier] <= TIER_RANK[selectedTier])

/**
 * Stages every product flag to match the selected package.
 *
 * Choosing a package sets what it includes on and everything above it off, so the
 * choice reads as "this practice is on Premium" rather than only ever taking things
 * away. Manual switches are left alone — they belong to no package, and that holds for
 * the requirement check as well as the staging: no package product flag sits behind a
 * manual one, so correcting a manual pair here could only ever edit a row the operator
 * did not touch.
 *
 * What the packages do own is then held to the same requirements the switches enforce,
 * so a package cannot stage a combination the operator could not have reached by hand:
 * a practice with no Twilio subaccount gets neither Texting nor the VoiP and Quinn call
 * product flags behind it, rather than a set that fails at save time.
 *
 * Nothing is written here; the result is form state until the operator saves it.
 */
export const applyTierSelection = (
  values: ProductFlagFormValues,
  selectedTier: ProductFlagTier,
  storedAccountSid: string | null
): ProductFlagFormValues => {
  const staged: ProductFlagFormValues = { ...values }

  for (const row of PRODUCT_FLAG_ROWS) {
    if (row.tier === 'manual') {
      continue
    }
    staged[row.field] = TIER_RANK[row.tier] <= TIER_RANK[selectedTier]
  }

  // Texting identifies the subaccount by its account SID, so a package cannot switch it
  // on for a practice with none. Judged against what the organization has rather than
  // the field alone: a SID cleared to be retyped would otherwise take Texting and the
  // five product flags behind it off in one click.
  if (!hasUsableAccountSid(staged.twilio_account_sid, storedAccountSid)) {
    staged.twilio_enabled = false
  }

  return clampToRequirements(staged, PACKAGE_FIELDS)
}
